import { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/AuthContext";

const TYPE_TONE = {
  RENEWAL_ALERT: "bg-amber-500",
  RENEWAL_NUDGE: "bg-purple-500",
  FINANCE: "bg-green-500",
  AUDIT_ALERT: "bg-cyan-600",
  CASE_INTERNAL_NOTE_MENTION: "bg-teal-600",
  INFO: "bg-slate-500",
};

export default function NotificationBell() {
  const { user } = useAuth();
  const isClientUser = (user?.roles || []).some((r) => (r || "").toLowerCase().startsWith("client"));
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    if (isClientUser) return;
    try {
      const r = await api.get("/notifications");
      setItems(r.data.data.notifications);
      setUnread(r.data.data.unread_count);
    } catch (_) { /* ignore */ }
  }, [isClientUser]);

  useEffect(() => { load(); }, [load]);

  // WebSocket live updates + 30s polling fallback (spec).
  useEffect(() => {
    if (!user || !user.id || isClientUser) return;
    let cancelled = false;
    let pollHandle = null;
    let ws = null;
    const startPolling = () => { pollHandle = setInterval(load, 30000); };
    (async () => {
      try {
        const r = await api.get("/auth/ws-token");
        const token = r.data?.data?.token;
        if (!token) throw new Error("no token");
        const base = API.replace(/^http/, "ws");
        ws = new WebSocket(`${base}/ws/notifications?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.event === "notification") {
              setItems((cur) => [msg.data, ...cur].slice(0, 30));
              setUnread((u) => u + 1);
            }
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => { if (!cancelled) startPolling(); };
        ws.onerror = () => { if (!cancelled) startPolling(); };
      } catch (_) {
        if (!cancelled) startPolling();
      }
    })();
    return () => {
      cancelled = true;
      if (pollHandle) clearInterval(pollHandle);
      if (ws) try { ws.close(); } catch (_) { /* ignore */ }
    };
  }, [user, load]);

  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); setUnread(0); setItems(items.map(i => ({ ...i, status: "READ" }))); } catch (_) { /* ignore */ }
  };
  const markOne = async (n) => {
    const id = n.id;
    // Mark read first (spec-aligned PATCH; server also accepts POST for
    // backward compat).
    try {
      await api.patch(`/notifications/${id}/read`);
    } catch (_) {
      try { await api.post(`/notifications/${id}/read`); } catch (_) { /* ignore */ }
    }
    setItems(items.map((i) => i.id === id
      ? { ...i, status: "READ", is_read: true }
      : i));
    setUnread((u) => Math.max(u - 1, 0));
    // Deep-link navigation — closes the popover and takes the user to
    // the case → Internal Notes tab (with optional note_id anchor). Use
    // `window.location.assign` (not react-router `navigate`) so the
    // Popover close animation cannot race the route change.
    setOpen(false);
    if (n.deep_link) {
      // Small deferral lets React flush the popover close before the
      // route change — otherwise Radix's focus-return can eat the click.
      setTimeout(() => { window.location.assign(n.deep_link); }, 0);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notifications-btn">
          <Bell className="w-4 h-4 text-slate-500" />
          {unread > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">{unread > 9 ? "9+" : unread}</span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-800">Notifications</div>
          {unread > 0 ? (
            <button className="text-xs text-cyan-700 font-semibold flex items-center gap-1 hover:underline" onClick={markAll} data-testid="mark-all-read"><CheckCheck className="w-3 h-3" />Mark all read</button>
          ) : null}
        </div>
        <ScrollArea className="max-h-96">
          <div className="divide-y divide-slate-100">
            {items.length === 0 ? <div className="px-4 py-10 text-center text-sm text-slate-500">No notifications yet.</div> : null}
            {items.map((n) => (
              <button key={n.id} onClick={() => markOne(n)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex gap-3 ${n.status === "UNREAD" ? "bg-cyan-50/30" : ""}`}
                      data-testid={`notif-${n.id}`}>
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${TYPE_TONE[n.notification_type] || "bg-slate-400"}`}></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{n.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>
                  <div className="flex items-center gap-2 mt-1">
                    {n.related_area ? (
                      <span className="text-[9px] uppercase tracking-widest text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-[1px]">
                        {n.related_area}
                      </span>
                    ) : null}
                    <span className="text-[10px] text-slate-400 font-mono">{n.created_at?.slice(0, 19).replace("T", " ")}</span>
                  </div>
                </div>
                {n.status === "UNREAD" ? <span className="text-[10px] uppercase font-bold text-cyan-600">New</span> : null}
              </button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
